import { Css_Text_20_30_To_16_24_tw } from '@dreamstack/feature-components/src/layout/Texts'
import type { BlockTextCloud as BlockTextCloudType } from '@dreamstack/payload/src/payload-types'
import { clsxMerge, cn } from '@dreamstack/util'
import indexOf from 'lodash/indexOf'
import map from 'lodash/map'
import type { FunctionComponent } from 'react'
import { useMemo } from 'react'

// Base class constants
export const BlockTextCloud_BlockTextTile_base_tw = cn(
  'bg-accentroGray-full text-accentroWhite-full! font-semibold max-w-xl justify-center px-px-24 pt-px-24 pb-px-64 m-px-4',
  'md:pt-px-48 md:px-px-40 md:max-w-[387px] md:pb-px-96',
  Css_Text_20_30_To_16_24_tw
)
export const BlockTextCloud_BlockTextTile_second_tw =
  'md:max-w-[347px] ml:max-w-[387px]'
export const BlockTextCloud_BlockTextTile_third_tw = 'md:max-w-[350px]'

export const BlockTextCloud_BlockWrapper_base_tw =
  'flex flex-col justify-center'
export const BlockTextCloud_BlockWrapper_row_tw = 'md:flex-row'
export const BlockTextCloud_BlockWrapper_grid_tw =
  'md:grid md:grid-cols-2 md:items-center ml:flex ml:flex-col'

export const BlockTextCloud_GroupOne_base_tw = 'flex flex-col'
export const BlockTextCloud_GroupOne_small_tw = 'md:flex-col md:items-end'
export const BlockTextCloud_GroupOne_large_tw =
  'md:items-end ml:flex-row ml:justify-center'

export const BlockTextCloud_GroupTwo_base_tw = 'flex flex-col'
export const BlockTextCloud_GroupTwo_small_tw =
  'md:flex-col md:items-start md:justify-center ml:items-end'
export const BlockTextCloud_GroupTwo_large_tw = 'ml:flex-row ml:items-start'

// Functional wrappers
const BlockTextTile = ({
  index,
  className,
  children,
}: {
  index: number
  className?: string
  children?: React.ReactNode
}) => {
  const isSecond = index === 1
  const isThird = index === 2
  return (
    <div
      className={clsxMerge(
        BlockTextCloud_BlockTextTile_base_tw,
        isSecond && BlockTextCloud_BlockTextTile_second_tw,
        isThird && BlockTextCloud_BlockTextTile_third_tw,
        className
      )}
    >
      {children}
    </div>
  )
}

const BlockWrapper = ({
  count,
  className,
  children,
}: {
  count: number
  className?: string
  children?: React.ReactNode
}) => (
  <div
    className={clsxMerge(
      BlockTextCloud_BlockWrapper_base_tw,
      count <= 3 && BlockTextCloud_BlockWrapper_row_tw,
      count > 3 && BlockTextCloud_BlockWrapper_grid_tw,
      className
    )}
  >
    {children}
  </div>
)

const GroupOne = ({
  count,
  className,
  children,
}: {
  count: number
  className?: string
  children?: React.ReactNode
}) => (
  <div
    className={clsxMerge(
      BlockTextCloud_GroupOne_base_tw,
      count <= 3 && BlockTextCloud_GroupOne_small_tw,
      count > 3 && BlockTextCloud_GroupOne_large_tw,
      className
    )}
  >
    {children}
  </div>
)

const GroupTwo = ({
  count,
  className,
  children,
}: {
  count: number
  className?: string
  children?: React.ReactNode
}) => (
  <div
    className={clsxMerge(
      BlockTextCloud_GroupTwo_base_tw,
      count <= 3 && BlockTextCloud_GroupTwo_small_tw,
      count > 3 && BlockTextCloud_GroupTwo_large_tw,
      className
    )}
  >
    {children}
  </div>
)

export const BlockTextCloud: FunctionComponent<
  React.PropsWithChildren<{
    block: BlockTextCloudType
  }>
> = ({ block }) => {
  const { groupOne, groupTwo, textFields } = useMemo(() => {
    const textFieldsRaw = [
      block.fieldName1,
      block.fieldName2,
      block.fieldName3,
      block.fieldName4,
      block.fieldName5,
    ]
    const textFields = textFieldsRaw.filter((t) => !!t)
    const groupOne: (string | null | undefined)[] = []
    const groupTwo: (string | null | undefined)[] = []
    textFields.forEach((t, i) =>
      i % 2 === 1 ? groupTwo.push(t) : groupOne.push(t)
    )
    return { groupOne, groupTwo, textFields }
  }, [
    block.fieldName1,
    block.fieldName2,
    block.fieldName3,
    block.fieldName4,
    block.fieldName5,
  ])

  const count = textFields.length

  return (
    textFields && (
      <div className="flex justify-center">
        <BlockWrapper count={count}>
          <GroupOne count={count}>
            {map(groupOne, (textField, i) => (
              <BlockTextTile key={i} index={indexOf(textFields, textField)}>
                <div>{textField}</div>
              </BlockTextTile>
            ))}
          </GroupOne>
          <GroupTwo count={count}>
            {map(groupTwo, (textField, i) => (
              <BlockTextTile key={i} index={indexOf(textFields, textField)}>
                <div>{textField}</div>
              </BlockTextTile>
            ))}
          </GroupTwo>
        </BlockWrapper>
      </div>
    )
  )
}
